import React from "react";
import { Link } from "react-router-dom";
import { Lock, Zap, ArrowRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";

// iter130 Phase A — UpgradeGate
//
// Wraps a page whose backend endpoints are Pro-gated. If the current
// user's subscription_tier is "pro" or "elite" (or role="admin"), we
// render the wrapped `children` unchanged. Otherwise we render a
// centered upsell card that keeps the surrounding chrome (top nav,
// sidebar, footer) but replaces the content — matching the "viewable
// but blocked" pattern the product spec approved.
//
// Enforcement is still server-side — this component is a *courtesy*
// UI, not a security control. Every gated endpoint returns 403 on the
// backend even if a determined user bypasses this wrapper.
export default function UpgradeGate({ feature = "trade_doctor", title, subtitle, children }) {
  const { user } = useAuth();

  const tier = user?.subscription_tier || user?.plan || "free";
  const isAdmin = user?.role === "admin";
  const hasAccess = isAdmin || tier === "pro" || tier === "elite";

  if (hasAccess) return <>{children}</>;

  const copy = {
    trade_doctor: {
      title: title || "Trade Doctor is a Pro feature",
      subtitle: subtitle || "Parse Delta / broker CSVs, get behavioural audits, and generate PDF reports — upgrade to unlock the full pipeline.",
      cta: "Unlock Trade Doctor",
    },
    ai_audit: {
      title: title || "AI Doctor Summary is a Pro feature",
      subtitle: subtitle || "The deterministic-first AI audit is included in the Pro plan.",
      cta: "Unlock AI Audit",
    },
    delta_sync: {
      title: title || "Delta auto-sync is a Pro feature",
      subtitle: subtitle || "Connect your Delta Exchange API for automatic trade + funding fee syncing every 15 minutes.",
      cta: "Unlock Delta Sync",
    },
  }[feature] || {
    title: title || "This is a Pro feature",
    subtitle: subtitle || "Upgrade to unlock.",
    cta: "Unlock",
  };

  return (
    <div
      data-testid={`upgrade-gate-${feature}`}
      className="max-w-xl mx-auto my-16 rounded-sm border border-[var(--bt-border)] bg-[var(--bt-surface)] p-8 space-y-5 text-center"
    >
      <span
        className="inline-flex w-14 h-14 rounded-sm items-center justify-center"
        style={{ background: "color-mix(in srgb, var(--bt-accent) 15%, transparent)" }}
      >
        <Lock className="w-7 h-7" style={{ color: "var(--bt-accent)" }} />
      </span>
      <div className="space-y-2">
        <p className="bt-label">
          <Zap className="w-3 h-3 inline mr-1" /> Pro
        </p>
        <h2 className="font-heading text-2xl">{copy.title}</h2>
        <p className="text-sm text-[var(--bt-text-muted)] leading-relaxed max-w-md mx-auto">
          {copy.subtitle}
        </p>
      </div>
      <div className="flex justify-center pt-1">
        <Link to="/pricing" data-testid={`upgrade-cta-${feature}`}>
          <Button
            className="h-11 px-6 rounded-sm bg-[var(--bt-accent)] hover:bg-[var(--bt-accent-hover)] text-black font-bold"
          >
            {copy.cta} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </div>
      <p className="text-[11px] text-[var(--bt-text-muted)]">
        Your Free plan includes the full Dashboard, calendar, Health Score, and manual trade logging — always free.
      </p>
    </div>
  );
}
